import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authApi } from '../utils/api';

const AuthContext = createContext(null);

// Câmpuri care NU se persistă în localStorage: sunt decise de server și se pot
// schimba fără ca browserul să afle (upgrade/downgrade Stripe, expirare reverse
// trial, schimbare făcută din alt device). Dacă le-am cache-ui, Navbar-ul ar
// arăta zile la rând un pachet pe care userul nu-l mai are.
const NON_CACHEABLE_FIELDS = ['tier', 'tier_started_at', 'previous_tier', 'previous_tier_started_at'];

const persistableUser = (u) => {
  if (!u) return u;
  const copy = { ...u };
  NON_CACHEABLE_FIELDS.forEach((k) => { delete copy[k]; });
  return copy;
};

const saveUser = (u) => {
  try { localStorage.setItem('brandvis_user', JSON.stringify(persistableUser(u))); } catch {}
};

// Nu lovim /profile mai des de atât la focus/refresh — altfel un user care face
// alt-tab des ar genera un request per comutare.
const PROFILE_REFRESH_MIN_INTERVAL_MS = 30 * 1000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const lastProfileFetchRef = useRef(0);

  // Reîncarcă profilul de pe server. `force` sare peste throttle (folosit la
  // montarea paginilor unde pachetul e informația principală: /account, /dashboard).
  const refreshProfile = useCallback(async ({ force = false } = {}) => {
    if (!localStorage.getItem('brandvis_token')) return null;
    const now = Date.now();
    if (!force && now - lastProfileFetchRef.current < PROFILE_REFRESH_MIN_INTERVAL_MS) return null;
    lastProfileFetchRef.current = now;
    try {
      const res = await authApi.profile();
      if (res?.data) {
        setUser(res.data);
        saveUser(res.data);
        return res.data;
      }
    } catch (e) {
      // 401 → interceptorul din api.js curăță tokenul + redirect.
    }
    return null;
  }, []);

  // 2026-08-19 — Magic link Supabase: tokenii vin in hash-ul URL-ului.
  //
  // Linkul de activare din email genera `redirect_to = {front}/dashboard`
  // (backend/routers/auth.py), Supabase raspundea cu
  // `/dashboard#access_token=…&refresh_token=…`, dar NIMIC din frontend nu citea
  // hash-ul acolo — singurul loc care o facea era ResetPasswordPage. Rezultat:
  // `brandvis_token` lipsea, PrivateRoute arunca userul la /login, si nici macar
  // bannerul `?activated=1` nu aparea (acela e pe ramura de fallback). Userul
  // care tocmai isi confirmase contul era intampinat cu un formular gol.
  //
  // Consumam hash-ul aici, in AuthProvider, ca sa acopere ORICE ruta de
  // aterizare, nu doar /dashboard. Exceptie: /reset-password, care isi
  // gestioneaza singura tokenul (are nevoie de el pentru updateUser, nu pentru
  // o sesiune normala).
  const consumeMagicLinkHash = () => {
    if (typeof window === 'undefined') return false;
    if (window.location.pathname.startsWith('/reset-password')) return false;
    const hash = window.location.hash || '';
    if (!hash.includes('access_token')) return false;

    const params = new URLSearchParams(hash.replace(/^#/, ''));
    const accessToken = params.get('access_token');
    if (!accessToken) return false;
    const refreshToken = params.get('refresh_token');

    try {
      localStorage.setItem('brandvis_token', accessToken);
      if (refreshToken) localStorage.setItem('brandvis_refresh_token', refreshToken);
    } catch {
      return false;
    }
    // Scoatem tokenii din bara de adrese — altfel raman in istoric si in orice
    // Referer trimis mai departe.
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    return true;
  };

  useEffect(() => {
    // Intai hash-ul: daca aducem o sesiune noua, ea are prioritate fata de cache.
    if (consumeMagicLinkHash()) {
      // Nu avem user cache-uit — il aducem de pe server inainte de a scoate
      // ecranul de loading, ca PrivateRoute sa nu vada `user=null` si sa
      // redirecteze la /login exact userul pe care tocmai l-am logat.
      refreshProfile({ force: true }).finally(() => setLoading(false));
      return;
    }

    const stored = localStorage.getItem('brandvis_user');
    const token = localStorage.getItem('brandvis_token');

    // Fara token nu exista sesiune — nimic de hidratat.
    if (!token) { setLoading(false); return; }

    if (stored) {
      try {
        // Hidratăm UI-ul imediat din localStorage (no flash). `tier` lipsește
        // intenționat din cache — vine din refreshProfile de mai jos.
        setUser(persistableUser(JSON.parse(stored)));
      } catch (e) {
        // 2026-08-19: cache-ul corupt NU mai invalideaza tokenul. Inainte
        // stergeam si `brandvis_token`, deci o singura scriere esuata in
        // localStorage (cota depasita, storage blocat) deloga userul fara
        // niciun mesaj. Stergem doar cache-ul si reconstruim profilul de pe
        // server, mai jos.
        console.warn('localStorage user data corrupted, refetching profile.', e);
        localStorage.removeItem('brandvis_user');
      }
      // În background, validăm tokenul + luăm tier-ul real.
      refreshProfile({ force: true });
      setLoading(false);
      return;
    }

    // Token valid, dar fara user cache-uit (saveUser inghite tacit exceptiile).
    // Reconstruim profilul inainte de a scoate ecranul de loading — altfel
    // PrivateRoute vede `user=null` si trimite la /login o sesiune buna.
    refreshProfile({ force: true }).finally(() => setLoading(false));
  }, [refreshProfile]);

  // Revenirea pe tab = moment natural de resincronizare: userul poate fi făcut
  // upgrade în portalul Stripe deschis în alt tab.
  useEffect(() => {
    const onFocus = () => { refreshProfile(); };
    const onVisible = () => { if (document.visibilityState === 'visible') refreshProfile(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refreshProfile]);

  const login = async (email, password) => {
    const res = await authApi.login(email, password);
    const { access_token, refresh_token, user: userData } = res.data;
    localStorage.setItem('brandvis_token', access_token);
    // 2026-05-14: Salvăm și refresh_token pentru auto-renew în timpul scanului (5+ min).
    // Inainte, scanul redirecta la /login dacă token-ul Supabase (default 1h) expira mid-scan.
    if (refresh_token) localStorage.setItem('brandvis_refresh_token', refresh_token);
    saveUser(userData);
    setUser(userData);
    lastProfileFetchRef.current = Date.now();
    return userData;
  };

  const logout = async () => {
    // Important: apelam logout-ul serverului INAINTE sa stergem tokenul local
    // (BE-ul are nevoie de Authorization header ca sa invalideze sesiunea).
    try { await authApi.logout(); } catch {}
    localStorage.removeItem('brandvis_token');
    localStorage.removeItem('brandvis_refresh_token');
    localStorage.removeItem('brandvis_user');
    // Cheile per-user — preferinte legate de contul curent. Le stergem ca
    // userul B sa nu vada preferintele lui A pe acelasi browser.
    [
      'pb_welcome_tour_seen',
      'pb_pinned_brands',
      'pb_hidden_public_brands',
      'pb_next_step_dismissed',
      'pb_onboarding_dismissed',
      'pb_upgrade_dismissed',
      'pb_streak',
      'pb_last_visit',
      // 2026-08-19 — intentia de deep link e legata de sesiunea care s-a
      // incheiat. Lasata in urma, pe un browser partajat userul B ateriza pe
      // ultimul URL al lui A (inofensiv server-side, dar randa gol).
      'brandvis_return_to',
      // Brandul preluat din mini-scan: daca userul se delogheaza fara sa-l
      // adauge, nu are ce cauta in contul urmatorului.
      'pb_pending_brand',
    ].forEach(k => { try { localStorage.removeItem(k); } catch {} });
    lastProfileFetchRef.current = 0;
    setUser(null);
  };

  const isAdmin = () => user?.role === 'admin';
  const isOnboarded = () => Boolean(user?.onboarded_at);

  // Actualizeaza profilul local fara sa refac login (folosit dupa onboarding sau update industrii)
  const updateUser = (patch) => {
    setUser(prev => {
      const next = { ...(prev || {}), ...patch };
      saveUser(next);
      return next;
    });
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, isAdmin, isOnboarded, updateUser, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
